/**
 * The Canvas2D renderer: the same FrameModel, drawn without WebGL2. It is
 * the ladder's last rung, the answer when WebGL2 is missing or lost, and
 * what `?gl=0` forces. The bit-sea becomes a CSS gradient behind the canvas
 * (the `fx-static-backdrop` class); ripples stay, as rings on the field.
 *
 * Every state keeps its shape, so hue is still never the only cue.
 */

import type { FrameModel, Unsub } from '../contracts';
import { CAM } from './camera';
import { BIT, FLAG, SHAPE, unpackStack } from './frame';
import type { FxRenderer, Quality } from './gl';
import { DARK_THEME, type Rgb, type Theme } from './palette';
import { P_FLOATS, PK, STAGE } from './particles';

const css = (c: Rgb | ArrayLike<number>, a = 1): string =>
  `rgb(${Math.round((c[0] ?? 0) * 255)} ${Math.round((c[1] ?? 0) * 255)} ${Math.round((c[2] ?? 0) * 255)} / ${Math.max(0, Math.min(1, a)).toFixed(3)})`;

/** Stage units → canvas CSS px, through the frame's camera matrix. */
export const stageToCanvas = (cam: Float32Array, x: number, y: number): { x: number; y: number } => {
  const cx = (cam[0] as number) * x + (cam[4] as number) * y + (cam[12] as number);
  const cy = (cam[1] as number) * x + (cam[5] as number) * y + (cam[13] as number);
  const cw = (cam[3] as number) * x + (cam[7] as number) * y + (cam[15] as number);
  const W = cam[CAM.canvasW] ?? 1, H = cam[CAM.canvasH] ?? 1;
  return { x: ((cx / cw + 1) / 2) * W, y: ((1 - cy / cw) / 2) * H };
};

export const createCanvas2dRenderer = (): FxRenderer => {
  let canvas: HTMLCanvasElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;
  let theme: Theme = DARK_THEME;
  let size = { w: 1, h: 1, dpr: 1 };
  let drawCalls = 0;
  const qualityFns = new Set<(q: number) => void>();

  const rrect = (c: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
    c.beginPath();
    c.roundRect(x, y, w, h, Math.max(0, Math.min(r, w / 2, h / 2)));
  };

  const drawBit = (c: CanvasRenderingContext2D, b: Float32Array, o: number, cam: Float32Array, time: number) => {
    const a0 = stageToCanvas(cam, b[o] as number, b[o + 1] as number);
    const a1 = stageToCanvas(cam, (b[o] as number) + (b[o + 2] as number), (b[o + 1] as number) + (b[o + 3] as number));
    const x = a0.x, y = a0.y, w = a1.x - a0.x, h = a1.y - a0.y;
    if (w <= 0.2 || h <= 0.2) return;
    if (x > size.w + 40 || y > size.h + 40 || x + w < -40 || y + h < -40) return;
    const rgb: [number, number, number] = [b[o + 4] as number, b[o + 5] as number, b[o + 6] as number];
    const alpha = b[o + 7] as number;
    const state = Math.round(b[o + 9] as number);
    const shape = state & 15;
    const phase = b[o + 10] as number;
    const k = w / Math.max(1e-3, b[o + 2] as number);   // px per stage unit here
    const ring = Math.max(1, 1.5 * k);
    const r = Math.min(2 * k, w / 2);
    c.globalAlpha = 1;
    switch (shape) {
      case SHAPE.plate:
        rrect(c, x, y, w, h, 12 * k);
        c.fillStyle = css(rgb, alpha);
        c.fill();
        c.strokeStyle = css(theme.ink, 0.1 * alpha);
        c.lineWidth = 1;
        c.stroke();
        break;
      case SHAPE.free:
        rrect(c, x, y, w, h, r);
        c.fillStyle = css(theme.freeFill, alpha);
        c.fill();
        rrect(c, x + ring / 2, y + ring / 2, w - ring, h - ring, r);
        c.strokeStyle = css(rgb, alpha);
        c.lineWidth = ring;
        c.stroke();
        break;
      case SHAPE.held:
        rrect(c, x, y, w, h, r);
        c.fillStyle = css(rgb, alpha);
        c.fill();
        if (state & FLAG.pinned) {
          const s = Math.min(w, h);
          c.fillStyle = css(theme.bg, 0.9 * alpha);
          c.fillRect(x + w / 2 - 0.32 * s, y + h / 2 - 0.08 * s, 0.64 * s, 0.48 * s);
          c.beginPath();
          c.arc(x + w / 2, y + h / 2 - 0.1 * s, 0.2 * s, Math.PI, 0);
          c.strokeStyle = css(theme.bg, 0.9 * alpha);
          c.lineWidth = Math.max(1, 0.11 * s);
          c.stroke();
        }
        break;
      case SHAPE.intent: {
        rrect(c, x + ring / 2, y + ring / 2, w - ring, h - ring, r);
        c.strokeStyle = css(rgb, alpha);
        c.lineWidth = ring;
        c.stroke();
        c.save();
        rrect(c, x, y, w, h, r);
        c.clip();
        c.strokeStyle = css(rgb, 0.55 * alpha);
        c.lineWidth = Math.max(1, 0.2 * Math.min(w, h));
        c.beginPath();
        const step = 0.55 * Math.min(w, h);
        for (let t = -h; t < w + h; t += step) {
          c.moveTo(x + t, y + h);
          c.lineTo(x + t + h, y);
        }
        c.stroke();
        c.restore();
        break;
      }
      case SHAPE.proposed:
      case SHAPE.foreign:
        rrect(c, x, y, w, h, r);
        c.fillStyle = css(rgb, (shape === SHAPE.proposed ? 0.12 : 0.06) * alpha);
        c.fill();
        rrect(c, x + ring / 2, y + ring / 2, w - ring, h - ring, r);
        c.setLineDash(shape === SHAPE.proposed ? [2 * k, 2 * k] : [ring, 2 * k]);
        c.lineDashOffset = -phase * 4 * k;
        c.strokeStyle = css(rgb, alpha);
        c.lineWidth = ring;
        c.stroke();
        c.setLineDash([]);
        break;
      case SHAPE.shield: {
        const s = Math.min(w, h) / 2, cx = x + w / 2, cy = y + h / 2;
        const pulse = 0.75 + 0.25 * Math.sin(phase * Math.PI * 2);
        c.strokeStyle = css(theme.refuse, alpha * pulse);
        c.lineWidth = Math.max(1.2, 1.6 * k);
        c.beginPath();
        for (let i = 0; i < 6; i++) {
          const ang = Math.PI / 6 + (i * Math.PI) / 3;
          const px = cx + Math.cos(ang) * s * 1.1, py = cy + Math.sin(ang) * s * 1.1;
          if (i === 0) c.moveTo(px, py); else c.lineTo(px, py);
        }
        c.closePath();
        c.moveTo(cx - 0.34 * s, cy - 0.34 * s);
        c.lineTo(cx + 0.34 * s, cy + 0.34 * s);
        c.moveTo(cx - 0.34 * s, cy + 0.34 * s);
        c.lineTo(cx + 0.34 * s, cy - 0.34 * s);
        c.stroke();
        break;
      }
      case SHAPE.aim:
        rrect(c, x - 3 * k, y - 3 * k, w + 6 * k, h + 6 * k, r + 3 * k);
        c.strokeStyle = css(rgb, alpha * (0.6 + 0.4 * Math.sin(time * 5)));
        c.lineWidth = Math.max(1.5, 2 * k);
        c.stroke();
        break;
      case SHAPE.select:
        rrect(c, x - 2 * k, y - 2 * k, w + 4 * k, h + 4 * k, r + 2 * k);
        c.strokeStyle = css(theme.focus, alpha);
        c.lineWidth = Math.max(2, 2 * k);
        c.stroke();
        break;
      case SHAPE.hint:
        rrect(c, x - 1.5 * k, y - 1.5 * k, w + 3 * k, h + 3 * k, r + 1.5 * k);
        c.setLineDash([2.5 * k, 2.5 * k]);
        c.lineDashOffset = -time * 4 * k;
        c.strokeStyle = css(rgb, alpha);
        c.lineWidth = Math.max(1, 1.5 * k);
        c.stroke();
        c.setLineDash([]);
        break;
      case SHAPE.pending:
        rrect(c, x, y, w, h, r);
        c.fillStyle = css(theme.ink, (0.1 + 0.2 * (0.5 + 0.5 * Math.sin(time * 8 + phase * 6))) * alpha);
        c.fill();
        break;
      case SHAPE.dot:
        c.beginPath();
        c.arc(x + w / 2, y + h / 2, Math.max(0.8, 0.13 * Math.min(w, h)), 0, Math.PI * 2);
        c.fillStyle = css(rgb, alpha);
        c.fill();
        break;
      case SHAPE.highlight:
        rrect(c, x + 0.5, y + 0.5, w - 1, h - 1, r);
        c.strokeStyle = css(rgb, alpha);
        c.lineWidth = 1;
        c.stroke();
        break;
    }
    // seat pips
    const { count, seats } = unpackStack(b[o + 11] as number);
    if (seats > 1 && shape <= SHAPE.intent) {
      const n = Math.min(seats, 8), step = w / (n + 1), pr = Math.max(0.8, Math.min(0.11 * h, step * 0.32));
      for (let i = 0; i < n; i++) {
        c.beginPath();
        c.arc(x + step * (i + 1), y + h - 0.24 * h, pr, 0, Math.PI * 2);
        const col = shape === SHAPE.held ? theme.bg : theme.ink;
        if (i < count) { c.fillStyle = css(col, 0.9 * alpha); c.fill(); }
        else { c.strokeStyle = css(col, 0.9 * alpha); c.lineWidth = 0.9; c.stroke(); }
      }
    }
    // glow, cheaply: a soft halo for bits that are lighting up
    const glow = b[o + 8] as number;
    if (glow > 0.3 && shape === SHAPE.held) {
      c.globalAlpha = Math.min(0.5, glow * 0.4) * alpha;
      rrect(c, x - w * 0.3, y - h * 0.3, w * 1.6, h * 1.6, w * 0.6);
      c.fillStyle = css(rgb, 1);
      c.fill();
      c.globalAlpha = 1;
    }
  };

  const drawParticle = (c: CanvasRenderingContext2D, p: Float32Array, o: number, cam: Float32Array) => {
    const kind = p[o + 3] as number;
    const stage = kind >= STAGE;
    let x = p[o] as number, y = p[o + 1] as number, rad = p[o + 2] as number;
    if (stage) {
      const a = stageToCanvas(cam, x, y), b = stageToCanvas(cam, x + rad, y);
      x = a.x; y = a.y; rad = Math.abs(b.x - a.x);
    } else {
      x -= cam[CAM.canvasLeft] ?? 0;
      y -= cam[CAM.canvasTop] ?? 0;
    }
    const alpha = p[o + 7] as number;
    if (alpha <= 0.01 || rad <= 0.05) return;
    const col = css([p[o + 4] as number, p[o + 5] as number, p[o + 6] as number], alpha);
    const k = kind % STAGE;
    c.beginPath();
    if (k === PK.ring || k === PK.cursor) {
      c.arc(x, y, rad * 0.85, 0, Math.PI * 2);
      c.strokeStyle = col;
      c.lineWidth = Math.max(1, rad * 0.12);
      c.stroke();
      if (k === PK.cursor) {
        c.beginPath();
        c.arc(x, y, rad * 0.25, 0, Math.PI * 2);
        c.fillStyle = col;
        c.fill();
      }
      return;
    }
    if (k === PK.shard) {
      c.moveTo(x, y - rad);
      c.lineTo(x + rad * 0.6, y);
      c.lineTo(x, y + rad);
      c.lineTo(x - rad * 0.6, y);
      c.closePath();
    } else {
      c.arc(x, y, k === PK.trail ? rad * 0.6 : rad * 0.7, 0, Math.PI * 2);
    }
    c.fillStyle = col;
    c.fill();
  };

  const r: FxRenderer = {
    kind: 'canvas2d',
    mount(c) {
      canvas = c;
      try {
        ctx = c.getContext('2d', { alpha: true });
      } catch {
        ctx = null;
      }
      if (!ctx) return false;
      c.classList.add('fx-static-backdrop');
      return true;
    },
    draw(frame: FrameModel) {
      const c = ctx;
      if (!c || !canvas) return;
      const dpr = Math.min(2, Math.max(1, size.dpr));
      const cam = frame.camera;
      const time = cam[CAM.time] ?? 0;
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.clearRect(0, 0, canvas.width, canvas.height);
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawCalls = 0;
      // ripples, as rings on the field
      const left = cam[CAM.canvasLeft] ?? 0, top = cam[CAM.canvasTop] ?? 0;
      for (let i = 0; i < frame.rippleCount; i++) {
        const o = i * 8;
        const age = frame.ripples[o + 2] ?? 0;
        const a = Math.max(0, 1 - age / 2.6) ** 2;
        if (a <= 0.01) continue;
        const width = frame.ripples[o + 7] ?? 40;
        const far = Math.hypot(size.w, size.h);
        const radius = width < 0 ? Math.max(0, far - age * 380 * 2.2) : age * 380;
        c.beginPath();
        c.arc((frame.ripples[o] ?? 0) - left, (frame.ripples[o + 1] ?? 0) - top, radius, 0, Math.PI * 2);
        c.strokeStyle = css([frame.ripples[o + 4] ?? 0, frame.ripples[o + 5] ?? 0, frame.ripples[o + 6] ?? 0], 0.35 * a);
        c.lineWidth = Math.max(1, Math.abs(width) * 0.25);
        c.stroke();
        drawCalls++;
      }
      for (let i = 0; i < frame.bitCount; i++) drawBit(c, frame.bits, i * BIT.floats, cam, time);
      drawCalls += frame.bitCount;
      c.globalCompositeOperation = theme.dark ? 'lighter' : 'source-over';
      for (let i = 0; i < frame.particleCount; i++) drawParticle(c, frame.particles, i * P_FLOATS, cam);
      c.globalCompositeOperation = 'source-over';
      drawCalls += frame.particleCount;
    },
    resize(w, h, dpr) {
      size = { w, h, dpr };
      if (!canvas) return;
      const d = Math.min(2, Math.max(1, dpr));
      canvas.width = Math.max(1, Math.round(w * d));
      canvas.height = Math.max(1, Math.round(h * d));
    },
    get quality(): Quality {
      return 5;
    },
    onQuality(fn): Unsub {
      qualityFns.add(fn);
      return () => qualityFns.delete(fn);
    },
    setQuality() {},
    setTheme(t) {
      theme = t;
    },
    setReduced() {},
    onLost(): Unsub {
      return () => {};
    },
    get drawCalls() {
      return drawCalls;
    },
    destroy() {
      canvas?.classList.remove('fx-static-backdrop');
      ctx = null;
      canvas = null;
      qualityFns.clear();
    },
  };
  return r;
};
